import {
  Box,
  Button,
  Checkbox,
  Circle,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import FAIcon from "@components/FAIcon";
import dayjs from "dayjs";
import useAPI from "hooks/api";
import getPublicDownloadUrl from "libs/get-public-download-url";
import { useCallback, useEffect, useState } from "react";

const BanUserModal = ({ isOpen, onClose, onSuccess, creatorId }) => {
  const [userId, setUserId] = useState("");
  const [reason, setReason] = useState("");
  const [banType, setBanType] = useState("creator"); // "creator" or "global"
  const [isTemporaryBan, setIsTemporaryBan] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const api = useAPI();
  const toast = useToast();

  const handleSubmit = useCallback(async () => {
    if (!userId.trim() || !reason.trim()) {
      toast({
        title: "請填寫使用者ID和封鎖原因",
        status: "error",
        duration: 3000,
      });
      return;
    }

    setIsLoading(true);
    try {
      let expiresAtTimestamp = null; // Default: permanent ban
      
      if (banType === "creator" && isTemporaryBan) {
        // 10 minutes from now
        expiresAtTimestamp = dayjs().add(10, 'minute').unix() * 1000;
      }
      
      if (banType === "global") {
        await api.globalBanStreamUser(userId.trim(), reason.trim(), null); // Global bans are always permanent
      } else {
        await api.banStreamUser(creatorId, userId.trim(), reason.trim(), expiresAtTimestamp);
      }
      
      toast({
        title: "封鎖成功",
        status: "success",
        duration: 3000,
      });
      
      setUserId("");
      setReason("");
      setBanType("creator");
      setIsTemporaryBan(false);
      onClose();
      onSuccess();
    } catch (error) {
      console.error("Error banning user:", error);
      toast({
        title: "封鎖失敗",
        description: error.message || "請稍後再試",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [userId, reason, banType, isTemporaryBan, creatorId, api, toast, onClose, onSuccess]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>封鎖使用者</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb={4}>
            <FormLabel>使用者ID</FormLabel>
            <Input 
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="請輸入使用者ID"
            />
          </FormControl>
          <FormControl mb={4}>
            <FormLabel>封鎖範圍</FormLabel>
            <RadioGroup value={banType} onChange={setBanType}>
              <Stack direction="row">
                <Radio value="creator">僅在此主播封鎖</Radio>
                <Radio value="global">全域封鎖</Radio>
              </Stack>
            </RadioGroup>
            <Text fontSize="xs" color="gray.500" mt={1}>
              {banType === "global" ? "全域封鎖：封鎖用戶在整個平台的所有直播間" : "封鎖此主播：僅封鎖用戶在此主播的直播間"}
            </Text>
          </FormControl>
          {banType === "creator" && (
            <FormControl mb={4}>
              <Checkbox 
                isChecked={isTemporaryBan}
                onChange={(e) => setIsTemporaryBan(e.target.checked)}
              >
                臨時封鎖 (10分鐘)
              </Checkbox>
              <Text fontSize="xs" color="gray.500" mt={1}>
                {isTemporaryBan ? "封鎖10分鐘後自動解除" : "永久封鎖，需要手動解除"}
              </Text>
            </FormControl>
          )}
          <FormControl mb={4}>
            <FormLabel>封鎖原因</FormLabel>
            <Textarea 
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="請輸入封鎖原因"
              rows={3}
            />
          </FormControl>
          <Text fontSize="sm" color="gray.500">
            封鎖期限：{banType === "global" ? "永久" : (isTemporaryBan ? "10分鐘" : "永久")}
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="red" mr={3} onClick={handleSubmit} isLoading={isLoading}>
            封鎖
          </Button>
          <Button variant="ghost" onClick={onClose}>
            取消
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const GlobalBansDrawer = ({ isOpen, onClose }) => {
  const api = useAPI();
  const [globalBans, setGlobalBans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const toast = useToast();

  const loadGlobalBans = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      // Don't pass creatorId to get all bans (admin can see all)
      const result = await api.getStreamBans({
        limit: 50,
        nextCursor: reset ? null : nextCursor,
        includeExpired: false,
        // Don't pass creatorId - this allows admin to see all bans including global ones
      });
      
      if (result) {
        // Group bans by userId to remove duplicates
        const newBans = result.data || [];
        const combinedBans = reset ? newBans : [...globalBans, ...newBans];
        
        // Group by userId and combine ban information
        const groupedBans = combinedBans.reduce((acc, ban) => {
          if (!acc[ban.userId]) {
            acc[ban.userId] = {
              ...ban,
              reasons: [ban.reason],
              banCount: 1
            };
          } else {
            // Add new reason if not already present
            if (!acc[ban.userId].reasons.includes(ban.reason)) {
              acc[ban.userId].reasons.push(ban.reason);
            }
            acc[ban.userId].banCount++;
          }
          return acc;
        }, {});
        
        setGlobalBans(Object.values(groupedBans));
        setNextCursor(result.nextCursor);
        setHasMore(!!result.nextCursor);
      }
    } catch (error) {
      console.error("Error loading global bans:", error);
      toast({
        title: "載入全域封鎖列表失敗",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [api, nextCursor, toast, globalBans]);

  const handleUnbanGlobal = useCallback(async (userId, userName) => {
    if (!window.confirm(`確定要解除 ${userName} 的所有全域封鎖嗎？\n\n此操作將移除該使用者的所有全域封鎖記錄。`)) return;
    
    try {
      await api.unbanStreamUser(userId); // No creatorId = global unban
      toast({
        title: "解除全域封鎖成功",
        description: "已移除該使用者的所有全域封鎖",
        status: "success",
        duration: 3000,
      });
      loadGlobalBans(true);
    } catch (error) {
      console.error("Error unbanning user globally:", error);
      toast({
        title: "解除全域封鎖失敗",
        description: error.message || "請稍後再試",
        status: "error",
        duration: 5000,
      });
    }
  }, [api, toast, loadGlobalBans]);

  useEffect(() => {
    if (isOpen) {
      loadGlobalBans(true);
    }
  }, [isOpen, loadGlobalBans]);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="md" placement="bottom">
      <DrawerOverlay />
      <DrawerContent maxH="80vh">
        <DrawerHeader>全域封鎖列表</DrawerHeader>
        <DrawerCloseButton />
        <DrawerBody overflow="auto">
          {globalBans.length === 0 && !loading ? (
            <Text color="gray.500" textAlign="center" py={8}>
              目前沒有全域封鎖的使用者
            </Text>
          ) : (
            <SimpleGrid spacing={4}>
              {globalBans.map((ban) => (
                <Flex
                  key={ban.userId}
                  justifyContent="space-between"
                  alignItems="center"
                  p={4}
                  border="1px"
                  borderColor="gray.600"
                  borderRadius="md"
                >
                  <Flex alignItems="center" columnGap={4}>
                    <Circle
                      size={12}
                      backgroundColor="black"
                      backgroundImage={
                        ban.avatar ? `url('${getPublicDownloadUrl(ban.avatar)}')` : null
                      }
                      backgroundPosition="center"
                      backgroundSize="cover"
                    />
                    <Box>
                      <Text fontWeight="semibold">{ban.userName}</Text>
                      <Text fontSize="sm" color="gray.400">
                        {ban.userId}
                      </Text>
                      <Text fontSize="sm" color="gray.400">
                        原因: {ban.reasons.join(", ")}
                      </Text>
                      <Text fontSize="xs" color="orange.400">
                        {ban.banCount} 次全域封鎖
                      </Text>
                    </Box>
                  </Flex>
                  <Flex columnGap={2} alignItems="center">
                    <Button
                      size="sm"
                      colorScheme="green"
                      onClick={() => handleUnbanGlobal(ban.userId, ban.userName)}
                    >
                      解除全域封鎖
                    </Button>
                    <FAIcon cursor="pointer" group="solid" name="circle-info" />
                  </Flex>
                </Flex>
              ))}
            </SimpleGrid>
          )}
          
          {hasMore && (
            <Flex justifyContent="center" mt={4}>
              <Button
                onClick={() => loadGlobalBans(false)}
                isLoading={loading}
                disabled={loading}
              >
                載入更多
              </Button>
            </Flex>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export const BansList = ({ creatorId }) => {
  const api = useAPI();
  const [bans, setBans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isGlobalDrawerOpen, onOpen: onGlobalDrawerOpen, onClose: onGlobalDrawerClose } = useDisclosure();

  const loadBans = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const result = await api.getStreamBans({
        creatorId,
        limit: 50,
        nextCursor: reset ? null : nextCursor,
        includeExpired: false,
      });
      
      if (result) {
        // Group bans by userId to remove duplicates
        const newBans = result.data || [];
        const combinedBans = reset ? newBans : [...bans, ...newBans];
        
        // Group by userId and combine ban information
        const groupedBans = combinedBans.reduce((acc, ban) => {
          if (!acc[ban.userId]) {
            acc[ban.userId] = {
              ...ban,
              reasons: [ban.reason],
              banCount: 1
            };
          } else {
            // Add new reason if not already present
            if (!acc[ban.userId].reasons.includes(ban.reason)) {
              acc[ban.userId].reasons.push(ban.reason);
            }
            acc[ban.userId].banCount++;
          }
          return acc;
        }, {});
        
        setBans(Object.values(groupedBans));
        setNextCursor(result.nextCursor);
        setHasMore(!!result.nextCursor);
      }
    } catch (error) {
      console.error("Error loading bans:", error);
      toast({
        title: "載入封鎖列表失敗",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [api, creatorId, nextCursor, toast, bans]);

  const handleUnban = useCallback(async (userId, userName) => {
    if (!window.confirm(`確定要解除 ${userName} 在此主播的所有封鎖嗎？\n\n此操作將移除該使用者在此主播的所有封鎖記錄。`)) return;
    
    try {
      await api.unbanStreamUser(userId, creatorId);
      toast({
        title: "解除封鎖成功",
        description: "已移除該使用者在此主播的所有封鎖",
        status: "success",
        duration: 3000,
      });
      loadBans(true);
    } catch (error) {
      console.error("Error unbanning user:", error);
      toast({
        title: "解除封鎖失敗",
        description: error.message || "請稍後再試",
        status: "error",
        duration: 5000,
      });
    }
  }, [api, creatorId, toast, loadBans]);

  const handleBanSuccess = useCallback(() => {
    loadBans(true);
  }, [loadBans]);

  useEffect(() => {
    if (creatorId) {
      loadBans(true);
    }
  }, [creatorId, loadBans]);

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Text fontSize="lg" fontWeight="bold">直播封鎖列表</Text>
        <Flex columnGap={2}>
          <Button colorScheme="blue" size="sm" onClick={onGlobalDrawerOpen}>
            查看全域封鎖
          </Button>
          <Button colorScheme="red" size="sm" onClick={onOpen}>
            封鎖使用者
          </Button>
        </Flex>
      </Flex>
      
      {bans.length === 0 && !loading ? (
        <Text color="gray.500" textAlign="center" py={8}>
          目前沒有封鎖的使用者
        </Text>
      ) : (
        <SimpleGrid spacing={4}>
          {bans.map((ban) => (
            <Flex
              key={ban.userId}
              justifyContent="space-between"
              alignItems="center"
              p={4}
              border="1px"
              borderColor="gray.600"
              borderRadius="md"
            >
              <Flex alignItems="center" columnGap={4}>
                <Circle
                  size={12}
                  backgroundColor="black"
                  backgroundImage={
                    ban.avatar ? `url('${getPublicDownloadUrl(ban.avatar)}')` : null
                  }
                  backgroundPosition="center"
                  backgroundSize="cover"
                />
                <Box>
                  <Text fontWeight="semibold">{ban.userName}</Text>
                  <Text fontSize="sm" color="gray.400">
                    {ban.userId}
                  </Text>
                  <Text fontSize="sm" color="gray.400">
                    原因: {ban.reasons.join(", ")}
                  </Text>
                  {ban.banCount > 1 && (
                    <Text fontSize="xs" color="orange.400">
                      {ban.banCount} 次封鎖
                    </Text>
                  )}
                </Box>
              </Flex>
              <Flex columnGap={2} alignItems="center">
                <Button
                  size="sm"
                  colorScheme="green"
                  onClick={() => handleUnban(ban.userId, ban.userName)}
                >
                  解除此主播封鎖
                </Button>
                <FAIcon cursor="pointer" group="solid" name="circle-info" />
              </Flex>
            </Flex>
          ))}
        </SimpleGrid>
      )}
      
      {hasMore && (
        <Flex justifyContent="center" mt={4}>
          <Button
            onClick={() => loadBans(false)}
            isLoading={loading}
            disabled={loading}
          >
            載入更多
          </Button>
        </Flex>
      )}
      
      <BanUserModal
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={handleBanSuccess}
        creatorId={creatorId}
      />
      
      <GlobalBansDrawer
        isOpen={isGlobalDrawerOpen}
        onClose={onGlobalDrawerClose}
      />
    </Box>
  );
};